import { useReducer, useEffect, memo } from 'react'
import type { ReactNode } from 'react'
import { useKcMessage } from 'keycloakify/lib/i18n/useKcMessage'
import { useKcLanguageTag } from 'keycloakify/lib/i18n/useKcLanguageTag'
import type { KcContext } from '../kcContext'
import { assert } from 'keycloakify/lib/tools/assert'
// import type { KcLanguageTag } from "keycloakify/lib/i18n/KcLanguageTag";
import { getBestMatchAmongKcLanguageTag } from 'keycloakify/lib/i18n/KcLanguageTag'
// import { getKcLanguageTagLabel } from "keycloakify/lib/i18n/KcLanguageTag";
// import { useCallbackFactory } from "powerhooks/useCallbackFactory";
import { headInsert } from '../../tools/headInsert'
import { pathJoin } from '../../tools/pathJoin'
import { useConstCallback } from 'powerhooks/useConstCallback'
import type { KcTemplateProps } from 'keycloakify'
import { useCssAndCx } from 'tss-react'
import logo from '../../logo.svg'
import { Card, Alert, Form } from '@xanadu_ai/xanadu-component-library'

const AUTHENTICATE_MESSAGE = 'Authenticate to link your account with '

export type TemplateProps = {
  displayInfo?: boolean
  displayMessage?: boolean
  displayRequiredFields?: boolean
  displayWide?: boolean
  showAnotherWayIfPresent?: boolean
  headerNode: ReactNode
  showUsernameNode?: ReactNode
  formNode: ReactNode
  infoNode?: ReactNode
  /** If you write your own page you probably want
   * to avoid pulling the default theme assets.
   */
  doFetchDefaultThemeResources: boolean
  headerDescription?: string
} & { kcContext: KcContext } & KcTemplateProps

export const Template = memo((props: TemplateProps) => {
  const {
    displayInfo = false,
    displayMessage = true,
    displayRequiredFields = false,
    displayWide = false,
    showAnotherWayIfPresent = true,
    headerNode,
    showUsernameNode = null,
    formNode,
    infoNode = null,
    kcContext,
    doFetchDefaultThemeResources,
    headerDescription,
  } = props

  const { cx } = useCssAndCx()

  useEffect(() => {
    console.log('Rendering this page with react using keycloakify')
  }, [])

  const { msg } = useKcMessage()

  const { kcLanguageTag } = useKcLanguageTag()

  // const onChangeLanguageClickFactory = useCallbackFactory(([languageTag]: [KcLanguageTag]) => setKcLanguageTag(languageTag));

  // eslint-disable-next-line
  const onTryAnotherWayClick = useConstCallback(
    () => (
      document.forms['kc-select-try-another-way-form' as never].submit(), false
    )
  )

  const { realm, locale, auth, url, message } = kcContext

  useEffect(() => {
    if (message && message.summary.match(AUTHENTICATE_MESSAGE)) {
      message.summary = `${message.summary.replace(AUTHENTICATE_MESSAGE, 'You have an existing account with Xanadu Cloud. Sign in to link that account with ')}.`
    }
  }, [message])

  useEffect(() => {
    if (!realm.internationalizationEnabled) {
      return
    }

    assert(locale !== undefined)

    if (kcLanguageTag === getBestMatchAmongKcLanguageTag(locale.current)) {
      return
    }

    window.location.href = locale.supported.find(
      ({ languageTag }) => languageTag === kcLanguageTag
    )!.url
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kcLanguageTag])

  const [isExtraCssLoaded, setExtraCssLoaded] = useReducer(() => true, false)

  useEffect(() => {
    if (!doFetchDefaultThemeResources) {
      setExtraCssLoaded()
      return
    }

    let isUnmounted = false
    const cleanups: (() => void)[] = []

    const toArr = (x: string | readonly string[] | undefined) =>
      typeof x === 'string' ? x.split(' ') : x ?? []

    Promise.all(
      [
        ...toArr(props.stylesCommon).map((relativePath) =>
          pathJoin(url.resourcesCommonPath, relativePath)
        ),
        ...toArr(props.styles).map((relativePath) =>
          pathJoin(url.resourcesPath, relativePath)
        ),
      ]
        .reverse()
        .map((href) =>
          headInsert({
            type: 'css',
            href,
            position: 'prepend',
          })
        )
    ).then(() => {
      if (isUnmounted) {
        return
      }

      setExtraCssLoaded()
    })

    toArr(props.scripts).forEach((relativePath) =>
      headInsert({
        type: 'javascript',
        src: pathJoin(url.resourcesPath, relativePath),
      })
    )

    if (props.kcHtmlClass !== undefined) {
      const htmlClassList = document.getElementsByTagName('html')[0].classList

      const tokens = cx(props.kcHtmlClass).split(' ')

      htmlClassList.add(...tokens)

      cleanups.push(() => htmlClassList.remove(...tokens))
    }

    return () => {
      isUnmounted = true

      cleanups.forEach((f) => f())
    }
    // eslint-disable-next-line
  }, [props.kcHtmlClass])

  if (!isExtraCssLoaded) {
    return null
  }

  return (
    <div className={cx(props.kcLoginClass)}>
      <Card shadow="large">
        <>
          <header className={cx(props.kcFormHeaderClass)}>
            <div className="logo">
              <img src={logo} alt="Xanadu Logo" className="logo__image" />
              <div className="logo__text">XANADU CLOUD</div>
            </div>
            {!(
              auth !== undefined &&
              auth.showUsername &&
              !auth.showResetCredentials
            ) ? (
              displayRequiredFields ? (
                <div className={cx(props.kcContentWrapperClass)}>
                  <div className={cx(props.kcLabelWrapperClass, 'subtitle')}>
                    <span className="subtitle">
                      <span className="required">*</span>
                      {msg('requiredFields')}
                    </span>
                  </div>
                  <div className="col-md-10">
                    <h2 id="kc-page-title">{headerNode}</h2>
                    {headerDescription && (
                      <h6 id="kc-page-description">{headerDescription}</h6>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <h2 id="kc-page-title">{headerNode}</h2>
                  {headerDescription && (
                    <h6 id="kc-page-description">{headerDescription}</h6>
                  )}
                </>
              )
            ) : displayRequiredFields ? (
              <div className={cx(props.kcContentWrapperClass)}>
                <div className={cx(props.kcLabelWrapperClass, 'subtitle')}>
                  <span className="subtitle">
                    <span className="required">*</span> {msg('requiredFields')}
                  </span>
                </div>
                <div className="col-md-10">
                  {showUsernameNode}
                  <div className={cx(props.kcFormGroupClass)}>
                    <div id="kc-username">
                      <label id="kc-attempted-username">
                        {auth?.attemptedUsername}
                      </label>
                      <a id="reset-login" href={url.loginRestartFlowUrl}>
                        <div className="kc-login-tooltip">
                          <i className={cx(props.kcResetFlowIcon)}></i>
                          <span className="kc-tooltip-text">
                            {msg('restartLoginTooltip')}
                          </span>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {showUsernameNode}
                <div className={cx(props.kcFormGroupClass)}>
                  <div id="kc-username">
                    <label id="kc-attempted-username">
                      {auth?.attemptedUsername}
                    </label>
                    <a id="reset-login" href={url.loginRestartFlowUrl}>
                      <div className="kc-login-tooltip">
                        <i className={cx(props.kcResetFlowIcon)}></i>
                        <span className="kc-tooltip-text">
                          {msg('restartLoginTooltip')}
                        </span>
                      </div>
                    </a>
                  </div>
                </div>
              </>
            )}
          </header>
          <div id="kc-content">
            <div id="kc-content-wrapper">
              {displayMessage &&
                message !== undefined &&
                message.type !== 'warning' && (
                  <Alert type={message.type}>
                    <span
                      data-testid="feedback-text"
                      className="kc-feedback-text"
                      dangerouslySetInnerHTML={{
                        __html: message.summary,
                      }}
                    />
                  </Alert>
                )}
              {formNode}
              {auth !== undefined &&
                auth.showTryAnotherWayLink &&
                showAnotherWayIfPresent && (
                  <Form
                    formName="kc-select-try-another-way-form"
                    action={url.loginAction}
                    method="post"
                    onSubmit={onTryAnotherWayClick}
                  >
                    <div
                      className={cx(
                        displayWide && [
                          props.kcFormSocialAccountContentClass,
                          props.kcFormSocialAccountClass,
                        ]
                      )}
                    >
                      <div className={cx(props.kcFormGroupClass)}>
                        <input type="hidden" name="tryAnotherWay" value="on" />
                        <a
                          href="/#"
                          id="try-another-way"
                          onClick={onTryAnotherWayClick}
                        >
                          {msg('doTryAnotherWay')}
                        </a>
                      </div>
                    </div>
                  </Form>
                )}
              {displayInfo && (
                <div id="kc-info" className={cx(props.kcSignUpClass)}>
                  <div
                    id="kc-info-wrapper"
                    className={cx(props.kcInfoAreaWrapperClass)}
                  >
                    {infoNode}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      </Card>
    </div>
  )
})
